// react
import React from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useForm } from '@tanstack/react-form';
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui icons
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';

// material ui
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { ReadAccountDto } from '@/api/generated/recalc/endpoints/v1/accounts';
import {
  useGetAccountByIdQuery,
  useUpdateAccountMutation,
} from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import { AdministratorOnly, SuperuserOnly } from '@/components/access-control';

// components
import { setSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

type UpdateAccountMutationTrigger = ReturnType<
  typeof useUpdateAccountMutation
>[0];

export const Route = createFileRoute('/$accountId/_user/account/profile')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'account:title' },
});

function RouteComponent() {
  const { t } = useTranslation('account');
  const { accountId } = useParams({ strict: false });
  const { data: account } = useGetAccountByIdQuery(accountId);
  const [updateAccount, { isLoading }] = useUpdateAccountMutation();

  if (!account?.AccountId) {
    return (
      <Box p={2}>
        <Typography color="text.secondary" data-testid="no-account">
          {t?.('messages.noAccount') || 'No account found'}
        </Typography>
      </Box>
    );
  }

  return (
    <AccountProfileForm
      key={account.AccountId} // Force remount when account changes
      account={account}
      updateAccount={updateAccount}
      isLoading={isLoading}
    />
  );
}

// Separate component that only renders when account data is available
const AccountProfileForm: React.FC<{
  account: ReadAccountDto;
  updateAccount: UpdateAccountMutationTrigger;
  isLoading: boolean;
}> = ({ account, updateAccount, isLoading }) => {
  const { t } = useTranslation('account');
  const dispatch = useDispatch();

  const form = useForm({
    defaultValues: {
      name: account.Name || '',
      address: account.Address || '',
      billingAddress: account.BillingAddress || '',
    },
    onSubmit: async ({ value }) => {
      const result = await updateAccount({
        accountId: account.AccountId,
        Name: value.name,
        Address: value.address,
        BillingAddress: value.billingAddress,
      });

      if (!('error' in result)) {
        dispatch(
          setSelectedAccount({
            ...account,
            Name: value.name,
            Address: value.address,
            BillingAddress: value.billingAddress,
          }),
        );

        dispatch(
          showSnackbar({
            message:
              t?.('messages.saved') || 'Account profile updated successfully',
            severity: 'success',
          }),
        );
      }
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Card>
        <CardHeader
          title={t?.('details.title', { accountName: account?.Name })}
          slotProps={{
            title: { variant: 'h6' }
          }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <SuperuserOnly>
              <TextField
                label={
                  <Box display="flex" alignItems="center">
                    {t?.('details.input.account_id') || 'Account ID'}{' '}
                    <Tooltip
                      title={t?.('tooltips.adminFeature') || 'Admin feature'}
                    >
                      <AdminPanelSettingsRoundedIcon
                        fontSize="small"
                        sx={{ ml: 1 }}
                      />
                    </Tooltip>
                  </Box>
                }
                value={account.AccountId || ''}
                fullWidth
                disabled
                sx={{
                  ':hover': {
                    cursor: 'not-allowed',
                  },
                }}
                data-testid="profile-account-id"
              />
            </SuperuserOnly>

            <form.Field
              name="name"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? t?.('validation.accountNameRequired') ||
                      'Account name is required'
                    : value.length < 2
                      ? t?.('validation.accountNameMinLength') ||
                        'Account name must be at least 2 characters'
                      : undefined,
              }}
            >
              {(field) => (
                <AdministratorOnly>
                  <TextField
                    label={t?.('details.input.account_name') || 'Account Name'}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    fullWidth
                    error={!!field.state.meta.errors.length}
                    helperText={field.state.meta.errors.join(', ')}
                    slotProps={{
                      htmlInput: { 'data-testid': 'profile-account-name' }
                    }}
                  />
                </AdministratorOnly>
              )}
            </form.Field>

            <form.Field
              name="address"
              validators={{
                onChange: ({ value }) =>
                  value && value.length > 0 && value.length < 5
                    ? t?.('validation.addressMinLength') ||
                      'Address must be at least 5 characters'
                    : undefined,
              }}
            >
              {(field) => (
                <AdministratorOnly>
                  <TextField
                    label={t?.('details.input.address') || 'Address'}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    fullWidth
                    error={!!field.state.meta.errors.length}
                    helperText={field.state.meta.errors.join(', ')}
                    slotProps={{
                      htmlInput: { 'data-testid': 'profile-account-address' }
                    }}
                  />
                </AdministratorOnly>
              )}
            </form.Field>

            <form.Field
              name="billingAddress"
              validators={{
                onChange: ({ value }) =>
                  value && value.length > 0 && value.length < 5
                    ? t?.('validation.addressMinLength') ||
                      'Billing address must be at least 5 characters'
                    : undefined,
              }}
            >
              {(field) => (
                <AdministratorOnly>
                  <TextField
                    label={
                      t?.('details.input.billing_address') || 'Billing Address'
                    }
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    fullWidth
                    error={!!field.state.meta.errors.length}
                    helperText={field.state.meta.errors.join(', ')}
                    slotProps={{
                      htmlInput: { 'data-testid': 'profile-billing-address' }
                    }}
                  />
                </AdministratorOnly>
              )}
            </form.Field>
          </Stack>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <AdministratorOnly>
            <form.Subscribe
              selector={(state) => ({
                isDirty: state.isDirty,
                isValid: state.isValid,
                canSubmit: state.canSubmit,
              })}
            >
              {(state) => (
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={() => form.reset()}
                    disabled={isLoading || !state.isDirty}
                    data-testid="profile-cancel-button"
                  >
                    {t?.('actions.reset', { ns: 'common' }) || 'Cancel'}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading || !state.isDirty || !state.isValid}
                    data-testid="profile-save-button"
                  >
                    {isLoading
                      ? t?.('actions.saving', { ns: 'common' }) || 'Saving...'
                      : t?.('actions.save', { ns: 'common' }) || 'Save'}
                  </Button>
                </Box>
              )}
            </form.Subscribe>
          </AdministratorOnly>
        </CardActions>
      </Card>
    </form>
  );
};
